import { useEffect, useMemo, useState } from "react";
import { Truck } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import ProductImageWithHover from "@/features/purchase-orders/components/ProductImageWithHover";
import type { PurchaseOrder, PurchaseOrderItem } from "@/features/purchase-orders";

/** Every line at its full ordered quantity, the state the dialog opens in. */
const orderedDefaults = (items: PurchaseOrderItem[]): Record<string, string> =>
  Object.fromEntries(items.map((item) => [item.id, String(item.ordered_quantity)]));

/** A digits-only input box, read as the number of units going on the truck. */
const parseQty = (raw: string | undefined): number => {
  if (raw === undefined || raw === "") return 0;
  const parsed = parseInt(raw, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

interface ShipTransferDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: PurchaseOrder;
  items: PurchaseOrderItem[];
  sourceLocationName: string | null;
  destLocationName: string | null;
  isShipping: boolean;
  onShip: (items: Array<{ itemId: string; shippedQuantity: number }>) => Promise<unknown>;
}

/**
 * BLU-535 section D: shipping a transfer is a quantity per SKU, not a status flip.
 *
 * The dialog this replaces only asked for confirmation, so the whole order moved
 * or none of it did. Anything left behind on the truck had to be reconciled by
 * hand afterwards. Here the operator says what actually went, and the remainder
 * stays committed at the source until someone confirms it was not shipped.
 */
export function ShipTransferDialog({
  open,
  onOpenChange,
  order,
  items,
  sourceLocationName,
  destLocationName,
  isShipping,
  onShip,
}: ShipTransferDialogProps) {
  const [quantities, setQuantities] = useState<Record<string, string>>({});

  // `items` is a fresh array on every refetch of the order, so keying the reset
  // effect on it would wipe whatever the operator had typed each time a
  // background query settled. The set of LINES is what the defaults depend on,
  // so that is what the effect keys on: a stable string of the line ids.
  const itemIdKey = useMemo(() => items.map((item) => item.id).join(","), [items]);

  // Default every line to its full ordered quantity each time the dialog opens,
  // so the common case (everything ships) is one click and a re-open never
  // inherits a half-edited draft from a previous attempt.
  useEffect(() => {
    if (!open) return;
    setQuantities(orderedDefaults(items));
    // itemIdKey, not items: see above. `items` is read inside, deliberately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, itemIdKey]);

  const handleInputChange = (itemId: string, value: string, ordered: number) => {
    if (value !== "" && !/^\d+$/.test(value)) return;
    if (value !== "" && parseInt(value, 10) > ordered) return;
    setQuantities((prev) => ({ ...prev, [itemId]: value }));
  };

  const handleFillFromOrdered = () => {
    setQuantities(orderedDefaults(items));
  };

  const totals = useMemo(() => {
    let going = 0;
    let staying = 0;
    for (const item of items) {
      const qty = parseQty(quantities[item.id]);
      going += qty;
      staying += Math.max(item.ordered_quantity - qty, 0);
    }
    return { going, staying };
  }, [items, quantities]);

  const handleConfirm = async () => {
    const payload = items.map((item) => ({
      itemId: item.id,
      shippedQuantity: parseQty(quantities[item.id]),
    }));

    // Await the ship BEFORE closing so the disabled state actually engages and
    // a second click cannot fire a duplicate.
    await onShip(payload);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Ship Transfer</DialogTitle>
          <DialogDescription>
            Enter what is going on the truck for <strong>{order.po_number}</strong>. Those units are
            deducted from <strong>{sourceLocationName ?? "the source location"}</strong> and marked
            as in transit to <strong>{destLocationName ?? "the destination location"}</strong>. The
            source location's cost is captured for each item.
          </DialogDescription>
        </DialogHeader>

        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={handleFillFromOrdered} disabled={isShipping}>
            Fill from ordered
          </Button>
        </div>

        <div className="border rounded-lg overflow-hidden max-h-[45vh] overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[64px]"></TableHead>
                <TableHead>SKU</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Ordered</TableHead>
                <TableHead className="text-right w-[120px]">Shipping</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <ProductImageWithHover
                      imageUrl={item.image_url}
                      productSku={item.sku || ""}
                      size="medium"
                    />
                  </TableCell>
                  <TableCell className="font-medium">{item.sku}</TableCell>
                  <TableCell>
                    {item.title ? (
                      <div className="flex flex-col gap-0.5">
                        <span className="text-sm font-medium">{item.title}</span>
                        {[item.category, item.color, item.size].filter(Boolean).length > 0 && (
                          <span className="text-xs text-muted-foreground">
                            {[item.category, item.color, item.size].filter(Boolean).join(" · ")}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm text-muted-foreground">
                        {item.category || "-"} · {item.color || "-"} · {item.size || "-"}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {item.ordered_quantity.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <Input
                      type="text"
                      inputMode="numeric"
                      className="h-8 w-[100px] text-right ml-auto tabular-nums"
                      placeholder="0"
                      value={quantities[item.id] ?? ""}
                      onChange={(e) =>
                        handleInputChange(item.id, e.target.value, item.ordered_quantity)
                      }
                      disabled={isShipping}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="rounded-lg border bg-muted/30 px-4 py-3 space-y-1">
          <div className="flex items-baseline justify-between text-sm">
            <span className="text-muted-foreground">Units going</span>
            <span className="font-medium tabular-nums">{totals.going.toLocaleString()}</span>
          </div>
          {totals.staying > 0 && (
            <>
              <div className="flex items-baseline justify-between text-sm">
                <span className="text-muted-foreground">Units staying behind</span>
                <span className="font-medium tabular-nums">{totals.staying.toLocaleString()}</span>
              </div>
              <p className="text-xs text-muted-foreground pt-1">
                Those units stay committed at the source until someone confirms they were not
                shipped.
              </p>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isShipping}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              // The mutation hook already raises the destructive toast on
              // failure; catching here is what stops the rejection escaping as
              // an unhandled promise and keeps the dialog open to retry.
              void handleConfirm().catch(() => undefined);
            }}
            disabled={isShipping || totals.going === 0}
          >
            <Truck className="h-4 w-4 mr-2" />
            {isShipping ? "Shipping..." : "Confirm Ship"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ShipTransferDialog;
